<div class="page-content">
  <!-- Header Section -->
  <div class="page-title-container">
    <div class="breadcrumb-spacer"></div>
    <h1 class="page-title">Resource Management</h1>
  </div>

  <div class="users-container">
    <main class="users-main">
      <div class="users-content">
        <!-- Filters Section -->
        <mat-card class="filters-card">
          <mat-card-content>
            <form [formGroup]="filterForm" class="filters-form">
              <div class="filter-row">
                <!-- Search Field -->
                <mat-form-field appearance="outline" class="search-field">
                  <mat-label>Search users</mat-label>
                  <input
                    matInput
                    [(ngModel)]="searchQuery"
                    [ngModelOptions]="{ standalone: true }"
                    placeholder="Search by name or email..."
                    autocomplete="off"
                    (input)="onSearchInput()"
                  />
                  <mat-icon
                    matSuffix
                    [style.cursor]="searchQuery ? 'pointer' : 'default'"
                    (click)="clearSearch()"
                    >{{ searchQuery ? "close" : "search" }}</mat-icon
                  >
                </mat-form-field>

                <!-- Active Status Filter -->
                <mat-form-field appearance="outline" class="status-field">
                  <mat-label>Status</mat-label>
                  <mat-select
                    formControlName="isActive"
                    (selectionChange)="onFilterChange()"
                  >
                    <mat-option [value]="null">All Users</mat-option>
                    <mat-option [value]="true">Active Only</mat-option>
                    <mat-option [value]="false">Inactive Only</mat-option>
                  </mat-select>
                </mat-form-field>

                <!-- Reset Button -->
                <button
                  mat-stroked-button
                  type="button"
                  (click)="resetFilters()"
                  class="reset-filters-btn"
                  [disabled]="!hasFiltersChanged()"
                >
                  <mat-icon>replay</mat-icon>
                  Reset
                </button>
              </div>
            </form>
          </mat-card-content>
        </mat-card>

        <!-- Error State -->
        @if (error && !isLoading) {
          <app-error-state
            [message]="error"
            (retry)="retry()"
          ></app-error-state>
        }

        <!-- Users Table -->
        @if (!error) {
          <mat-card class="table-card no-padding">
            <div class="users-table-container">
              <!-- Loading State for Search Operations Only -->
              @if (isSearching) {
                <div class="loading-state">
                  <mat-spinner diameter="32"></mat-spinner>
                  <p>Searching users...</p>
                </div>
              }

              <!-- Users Table - Show for both loading (with skeleton) and loaded states -->
              @if (!isSearching) {
                <div class="table-wrapper">
                  <table
                    mat-table
                    [dataSource]="dataSource"
                    matSort
                    matSortActive="lastLogin"
                    matSortDirection="desc"
                    (matSortChange)="onSortChange($event)"
                    class="users-table"
                  >
                    <!-- Name Column (with email as sub-text) -->
                    <ng-container matColumnDef="name">
                      <th
                        mat-header-cell
                        *matHeaderCellDef
                        mat-sort-header="name"
                      >
                        Name
                      </th>
                      <td mat-cell *matCellDef="let user" class="name-cell">
                        @if (isLoading) {
                          <div class="skeleton-line"></div>
                        } @else {
                          <div class="name-cell-content">
                            <span class="resource-name">{{
                              getUserDisplayName(user)
                            }}</span>
                            @if (hasDisplayName(user) && user.email) {
                              <span class="resource-email">{{
                                user.email
                              }}</span>
                            }
                          </div>
                        }
                      </td>
                    </ng-container>

                    <!-- Roles Column -->
                    <ng-container matColumnDef="roles">
                      <th mat-header-cell *matHeaderCellDef>
                        <div class="role-header-container">
                          <span>Role</span>
                          <mat-icon
                            class="role-info-icon"
                            (mouseenter)="showRoleInfoHoverCard($event)"
                            (mouseleave)="hideRoleInfoHoverCard()"
                          >
                            help
                          </mat-icon>
                        </div>
                      </th>
                      <td mat-cell *matCellDef="let user" class="roles-cell">
                        @if (isLoading) {
                          <div class="skeleton-line"></div>
                        } @else {
                          <div class="role-dropdown-container">
                            @if (user.isResourceOnly && isCurrentUserAdmin) {
                              <div
                                style="
                                  display: flex;
                                  align-items: center;
                                  gap: 8px;
                                "
                              >
                                <mat-form-field
                                  appearance="outline"
                                  class="role-form-field"
                                >
                                  <mat-select
                                    [value]="getUserCurrentRoleId(user)"
                                    [disabled]="true"
                                  >
                                    @for (role of roles; track role.id) {
                                      <mat-option [value]="role.id">
                                        {{ role.name }}
                                      </mat-option>
                                    }
                                  </mat-select>
                                </mat-form-field>
                                <mat-icon
                                  class="role-info-icon last-super-admin-tooltip"
                                  matTooltip="This person hasn't logged in yet. Only users who have signed in can have their role changed."
                                  matTooltipPosition="right"
                                  matTooltipClass="centered-tooltip"
                                >
                                  help
                                </mat-icon>
                              </div>
                            } @else if (user.isResourceOnly) {
                              <div class="role-display-only">
                                <span class="role-text">{{
                                  getUserCurrentRoleName(user) || "Basic"
                                }}</span>
                              </div>
                            } @else if (isCurrentUserAdmin) {
                              <div
                                style="
                                  display: flex;
                                  align-items: center;
                                  gap: 8px;
                                "
                              >
                                <mat-form-field
                                  appearance="outline"
                                  class="role-form-field"
                                >
                                  <mat-select
                                    [value]="getUserCurrentRoleId(user)"
                                    (selectionChange)="
                                      onRoleChange(user, $event.value)
                                    "
                                    [disabled]="isRoleDropdownDisabled(user)"
                                    (click)="$event.stopPropagation()"
                                  >
                                    @for (role of roles; track role.id) {
                                      <mat-option [value]="role.id">
                                        {{ role.name }}
                                      </mat-option>
                                    }
                                  </mat-select>
                                </mat-form-field>
                                @if (
                                  snackbarService.isLoading(
                                    "user-role-" + user.id
                                  )
                                ) {
                                  <mat-spinner
                                    diameter="20"
                                    class="inline-loading-spinner"
                                  ></mat-spinner>
                                }
                                @if (isLastSuperAdmin(user)) {
                                  <mat-icon
                                    class="role-info-icon last-super-admin-tooltip"
                                    [matTooltip]="
                                      'You are the last remaining Super Admin, so you cannot switch your role unless there are at least two Super Admins already in the system.'
                                    "
                                    matTooltipPosition="right"
                                    matTooltipClass="centered-tooltip"
                                  >
                                    help
                                  </mat-icon>
                                }
                              </div>
                            } @else {
                              <div class="role-display-only">
                                <span class="role-text">{{
                                  getUserCurrentRoleName(user) || "No Role"
                                }}</span>
                              </div>
                            }
                          </div>
                        }
                      </td>
                    </ng-container>

                    <!-- Practice Areas Column -->
                    <ng-container matColumnDef="practiceAreas">
                      <th mat-header-cell *matHeaderCellDef>Practice Areas</th>
                      <td
                        mat-cell
                        *matCellDef="let user"
                      >
                        @if (isLoading) {
                          <div class="skeleton-chips">
                            <div class="skeleton-chip"></div>
                            <div class="skeleton-chip"></div>
                            <div class="skeleton-chip"></div>
                          </div>
                        } @else if (getUserPracticeAreaChips(user).length > 0) {
                          <app-membership-chips
                            [items]="getUserPracticeAreaChips(user)"
                            [maxVisible]="1"
                            [differentiatePrimary]="true"
                          />
                        } @else {
                          <span class="text-muted">—</span>
                        }
                      </td>
                    </ng-container>

                    <!-- Offices Column -->
                    <ng-container matColumnDef="offices">
                      <th mat-header-cell *matHeaderCellDef>Offices</th>
                      <td
                        mat-cell
                        *matCellDef="let user"
                      >
                        @if (isLoading) {
                          <div class="skeleton-chips">
                            <div class="skeleton-chip"></div>
                            <div class="skeleton-chip"></div>
                          </div>
                        } @else if (getUserOfficeChips(user).length > 0) {
                          <app-membership-chips
                            [items]="getUserOfficeChips(user)"
                            [maxVisible]="1"
                            [differentiatePrimary]="true"
                          />
                        } @else {
                          <span class="text-muted">—</span>
                        }
                      </td>
                    </ng-container>

                    <!-- Visibility Column (shows the resource's current setting) -->
                    <ng-container matColumnDef="visibility">
                      <th mat-header-cell *matHeaderCellDef>Visibility</th>
                      <td
                        mat-cell
                        *matCellDef="let user"
                        class="visibility-cell"
                      >
                        @if (isLoading) {
                          <div class="skeleton-line short"></div>
                        } @else {
                          @let visibility = getResourceVisibilityDisplay(user);
                          <div class="visibility-value">
                            <span class="visibility-label">{{
                              visibility.label
                            }}</span>
                            @if (visibility.isDefault) {
                              <span class="visibility-source">Default</span>
                            }
                          </div>
                        }
                      </td>
                    </ng-container>

                    <!-- Last Login Column -->
                    <ng-container matColumnDef="lastLogin">
                      <th
                        mat-header-cell
                        *matHeaderCellDef
                        mat-sort-header="lastLogin"
                      >
                        Last Login
                      </th>
                      <td
                        mat-cell
                        *matCellDef="let user"
                        class="last-login-cell"
                      >
                        @if (isLoading) {
                          <div class="skeleton-line short"></div>
                        } @else if (user.lastLogin) {
                          <span class="last-login-date">{{
                            formatLastLogin(user.lastLogin)
                          }}</span>
                        } @else {
                          <span class="text-muted">Never</span>
                        }
                      </td>
                    </ng-container>

                    <!-- Status Column -->
                    <ng-container matColumnDef="status">
                      <th
                        mat-header-cell
                        *matHeaderCellDef
                        mat-sort-header="status"
                      >
                        Status
                      </th>
                      <td mat-cell *matCellDef="let user" class="status-cell">
                        @if (isLoading) {
                          <div class="skeleton-chip"></div>
                        } @else {
                          <span
                            class="badge"
                            [ngClass]="
                              user.isActive ? 'badge-active' : 'badge-inactive'
                            "
                          >
                            {{ user.isActive ? "Active" : "Inactive" }}
                          </span>
                        }
                      </td>
                    </ng-container>

                    <!-- Actions Column -->
                    @if (showActionsColumn) {
                      <ng-container matColumnDef="actions">
                        <th
                          mat-header-cell
                          *matHeaderCellDef
                          class="actions-header"
                        >
                        </th>
                        <td
                          mat-cell
                          *matCellDef="let user"
                          class="actions-cell"
                        >
                          @if (isLoading) {
                            <div class="skeleton-line short"></div>
                          } @else {
                            <div class="actions-cell-inner">
                              @if (!user.employeeId) {
                                <mat-icon
                                  class="no-employee-warning"
                                  matTooltip="There is a problem with this user's employee ID. Please contact support."
                                  matTooltipPosition="left"
                                >
                                  warning
                                </mat-icon>
                              } @else {
                                @if (isCurrentUserAdmin) {
                                  <button
                                    mat-icon-button
                                    type="button"
                                    class="action-btn"
                                    matTooltip="Set resource visibility"
                                    (click)="openVisibilityDialog(user)"
                                  >
                                    <mat-icon>visibility</mat-icon>
                                  </button>
                                }
                                @if (canManagePracticeAreas) {
                                  <button
                                    mat-icon-button
                                    type="button"
                                    class="action-btn"
                                    matTooltip="Manage practice areas"
                                    (click)="openPracticeAreasDialog(user)"
                                  >
                                    <mat-icon>category</mat-icon>
                                  </button>
                                }
                                @if (canManageOffices) {
                                  <button
                                    mat-icon-button
                                    type="button"
                                    class="action-btn"
                                    matTooltip="Manage offices"
                                    (click)="openOfficesDialog(user)"
                                  >
                                    <mat-icon>location_city</mat-icon>
                                  </button>
                                }
                              }
                            </div>
                          }
                        </td>
                      </ng-container>
                    }

                    <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                    <tr
                      mat-row
                      *matRowDef="let row; columns: displayedColumns"
                    ></tr>

                    <!-- Empty State -->
                    <tr class="mat-row" *matNoDataRow>
                      <td
                        class="mat-cell no-data-cell"
                        [attr.colspan]="displayedColumns.length"
                      >
                        <div class="empty-state">
                          <mat-icon>search_off</mat-icon>
                          <span>No users found matching your search</span>
                        </div>
                      </td>
                    </tr>
                  </table>
                </div>

                <!-- Paginator -->
                <mat-paginator
                  [length]="totalCount"
                  [pageSize]="pageSize"
                  [pageIndex]="pageIndex"
                  [pageSizeOptions]="[10, 25, 50, 100]"
                  (page)="onPageChange($event)"
                  showFirstLastButtons
                  class="users-paginator"
                >
                </mat-paginator>
              }
            </div>
          </mat-card>
        }
      </div>
    </main>

    <!-- Role Info Hover Card -->
    @if (showRoleInfoHover) {
      <div
        class="role-info-hover-card"
        [style.left.px]="roleInfoHoverPosition.x"
        [style.top.px]="roleInfoHoverPosition.y"
      >
        <mat-card class="hover-card-content">
          <mat-card-content>
            <div class="role-info-header">
              <mat-icon class="info-icon">info</mat-icon>
              <h3>Role Permissions</h3>
            </div>
            <div class="role-info-body">
              @for (role of roles; track role.id) {
                <div class="role-permission-item">
                  <div
                    class="badge badge-role"
                    [class.protected-admin]="role.isProtectedAdmin"
                  >
                    {{ role.name }}
                    @if (role.isProtectedAdmin) {
                      <mat-icon class="protected-icon">shield</mat-icon>
                    }
                  </div>
                  <div class="role-description">
                    {{ role.description || "No description available" }}
                  </div>
                  <div class="permissions-list">
                    @for (
                      permission of getRolePermissions(role);
                      track permission.label;
                      let i = $index
                    ) {
                      <div class="permission-wrapper">
                        <div class="permission-item">
                          <span class="permission-number"
                            >{{ toRomanNumeral(i) }}.</span
                          >
                          <span class="permission-label"
                            >{{ permission.label }}:</span
                          >
                          <span
                            class="permission-value"
                            [class.value-yes]="permission.value === 'Yes'"
                            [class.value-no]="permission.value === 'No'"
                          >
                            {{ permission.value }}
                          </span>
                        </div>
                        @if (permission.subItems) {
                          <div class="permission-subitems">
                            @for (
                              subItem of permission.subItems;
                              track subItem.label;
                              let j = $index
                            ) {
                              <div class="permission-subitem">
                                <span class="subitem-number">{{ j + 1 }}.</span>
                                <span class="permission-label"
                                  >{{ subItem.label }}:</span
                                >
                                <span
                                  class="permission-value"
                                  [class.value-yes]="subItem.value === 'Yes'"
                                  [class.value-no]="subItem.value === 'No'"
                                >
                                  {{ subItem.value }}
                                </span>
                              </div>
                            }
                          </div>
                        }
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    }
  </div>
</div>
